import { useSyncExternalStore, useCallback, useEffect } from "react";
import { useQueryClient } from "@tanstack/react-query";

const STORAGE_KEY = "adva:impersonate-business-id";

// `key === null` when the whole store is cleared (localStorage.clear()).
function isImpersonationStorageEvent(e: StorageEvent): boolean {
  return e.key === STORAGE_KEY || e.key === null;
}

function subscribe(callback: () => void): () => void {
  const handler = (e: StorageEvent) => {
    if (isImpersonationStorageEvent(e)) callback();
  };
  window.addEventListener("storage", handler);
  return () => window.removeEventListener("storage", handler);
}

function getSnapshot(): string | null {
  return localStorage.getItem(STORAGE_KEY);
}

function getServerSnapshot(): null {
  return null;
}

/**
 * Drop every cached query whenever the impersonated business changes — in
 * *any* tab. Same-tab changes arrive via the synthetic StorageEvent that
 * start/stopImpersonation dispatch; cross-tab changes arrive as native
 * `storage` events.
 *
 * We `resetQueries()` rather than `invalidateQueries()`: tenant-scoped query
 * keys do NOT include the active business (e.g. `useCustomers` keys on
 * `["customers", params]`), so merely marking entries stale leaves the
 * previous tenant's rows observable while the refetch is in flight — and
 * retained indefinitely if that refetch errors. Resetting evicts the cached
 * data (observers fall back to pending) before refetching, so a background
 * tab can never render the prior tenant's data (a tenant-isolation smell —
 * ADV-1460).
 *
 * Mount this EXACTLY ONCE, at the app root (App.tsx, alongside the other
 * render-less providers), NOT inside `useImpersonation`. The routed tree is
 * split across sibling shells — `AppLayoutV2` (`/`) and `V2SettingsLayout`
 * (`/settings/*`) — and impersonation is both triggered from and observed on
 * both, so a per-shell or per-consumer mount would either miss a tree or, on
 * pages like /settings/adva/account-users where `useImpersonation` is called
 * once per user row, register N listeners and fire N whole-cache resets for a
 * single impersonation change (avoidable refetch fan-out). One app-root
 * listener covers every protected page with a single reset.
 */
export function useImpersonationCacheInvalidation(): void {
  const queryClient = useQueryClient();
  useEffect(() => {
    const handler = (e: StorageEvent) => {
      if (isImpersonationStorageEvent(e)) queryClient.resetQueries();
    };
    window.addEventListener("storage", handler);
    return () => window.removeEventListener("storage", handler);
  }, [queryClient]);
}

export function useImpersonation() {
  const impersonatedBusinessId = useSyncExternalStore(
    subscribe,
    getSnapshot,
    getServerSnapshot,
  );

  const startImpersonation = useCallback((businessId: string) => {
    localStorage.setItem(STORAGE_KEY, businessId);
    window.dispatchEvent(new StorageEvent("storage", { key: STORAGE_KEY }));
  }, []);

  const stopImpersonation = useCallback(() => {
    localStorage.removeItem(STORAGE_KEY);
    window.dispatchEvent(new StorageEvent("storage", { key: STORAGE_KEY }));
  }, []);

  return { impersonatedBusinessId, startImpersonation, stopImpersonation };
}
